/**
 * PIN = БҮРЭН НЭВТРЭХ ИТГЭМЖЛЭЛ (2026-09-27, docs/08 §PIN-ээр бүрэн нэвтрэх, docs/35 §PIN итгэмжлэл).
 *
 * ! Панелийн ажилтны код + PIN нэвтрэлт одоо хэрэглэгчийн ЖИНХЭНЭ эрхийг өгнө (менежер → Manager түвшин). Урьд нь
 * ! `APP_ALLOWED_PERMISSIONS`-ээс гадуурх эрхтэй бүртгэлд PIN хаалттай байсан нь «ХН-ийн ажилтан админы PIN тавиад
 * ! админ болох» цоорхойг хаадаг байв (AUTHZ-07/AUTH-2). Тэр хаалтыг авсан тул ЭНЭ хаалга түүнийг орлоно: хэн хэний
 * ! PIN (итгэмжлэл)-ийг тавьж / холбож болохыг API (403) ба админ (товч нуух/тайлбар) ИЖИЛ дүрмээр шийднэ.
 */

/**
 * Эрх мэдэлтэй (панелийн, `APP_ALLOWED_PERMISSIONS`-ээс гадуурх эрхтэй) хэрэглэгчийн PIN-ийг тавих / дахин тохируулах,
 * PIN-тэй ажилтанд эрх мэдэлтэй хэрэглэгч холбох эрх. `hr.employees:edit` ДАНГААРАА хүрэхгүй.
 *
 * ! seed: `super-admin` ('*'), `admin` (бүгд − `roles:delete`) — ALL_PERMISSION_KEYS-ээр автоматаар. `manager`,
 * ! `hr-manager`-т ЗОРИУД өгөхгүй. Апп (`sid`) сессийн зөвшөөрөх жагсаалтад ОРОХГҮЙ.
 */
export const HR_EMPLOYEE_CREDENTIALS_PERMISSION = 'hr.employees:credentials' as const

/** `super-admin` дүрийн түлхүүр — `apps/api/src/users/user-authority.ts` `SUPER_ADMIN_ROLE`-той ЯГ ижил. */
const SUPER_ADMIN_ROLE_KEY = 'super-admin'

/**
 * Итгэмжлэл бичих зам:
 *   `hr_reset` — `POST /hr/employees/:id/pin` (ХН/админ, одоогийн PIN шаардахгүй);
 *   `link`     — хэрэглэгч↔ажилтан холбох (`PATCH /hr/employees/:id/user`, `PATCH /users/:id {employeeId}`):
 *                PIN-тэй ажилтанд холбох = тэр PIN-ийг хэрэглэгчийн итгэмжлэл болгох.
 */
export type PinCredentialWriteVia = 'hr_reset' | 'link'

/**
 * `self`        — өөрийн ажилтны PIN-ийг админы замаар (одоогийн итгэмжлэлгүй) тавих оролдлого (403). Өөрийнхөө PIN-ийг
 *                 `POST /auth/change-pin`-ээр (одоогийн PIN эсвэл нууц үгээр) солино. `link`-д үйлчлэхгүй.
 * `permission`  — зорилтот хэрэглэгч эрх мэдэлтэй, үйлдэгчид `hr.employees:credentials` алга (403).
 *
 * ! Энэ нь ЗӨВХӨН эхний хаалга. API үүний ДАРАА `assertMayManageUser`-ийг (супер админыг зөвхөн супер админ, зорилтын
 * ! панелийн эрх бүрийг эзэмшигч) заавал дуудна — энэ функц тэр дүрмийг давтахгүй.
 */
export type PinCredentialWriteProblem = 'self' | 'permission'

export const pinCredentialWriteProblem = (input: {
  via: PinCredentialWriteVia
  actorUserId: string
  actorPermissions: readonly string[] | null | undefined
  actorRoleKeys: readonly string[] | null | undefined

  /** Ажилтанд холбоотой (эсвэл холбогдох гэж буй) хэрэглэгч. `null` — хэрэглэгчгүй (анх нэвтрэхэд `hr-employee` үүснэ). */
  targetUserId: string | null

  /** Зорилтот хэрэглэгч `APP_ALLOWED_PERMISSIONS`-ээс гадуурх эрхтэй эсэх (API: `isPrivilegedAccount`). */
  targetPrivileged: boolean
}): PinCredentialWriteProblem | null => {
  if (input.targetUserId === null) return null
  if (input.via === 'hr_reset' && input.targetUserId === input.actorUserId) return 'self'
  if (!input.targetPrivileged) return null
  if (input.actorRoleKeys?.includes(SUPER_ADMIN_ROLE_KEY)) return null
  if (!input.actorPermissions?.includes(HR_EMPLOYEE_CREDENTIALS_PERMISSION)) return 'permission'

  return null
}
